<style scoped>
  .breadcumb {
    margin: 0 0 1em;
  }

  .problem {
    margin: 0 0 1em;
  }

  object.pdf {
    display: block;
    height: 90vh;
    width: 100%;
  }

  .ruler {
    border-bottom: 1px solid #222;
    margin-bottom: 4px;
  }
</style>

<template>
  <div>
    <NavBar />

    <div class="container">
      <div v-if="problem">
        <div class="breadcumb">
          <router-link to="/assignments">Assignments</router-link>
          /
          <router-link :to="`/assignments/${assignment.id}`">
            {{ assignment.title }}
          </router-link>
          /
          {{ problem.title }}
        </div>

        <div class="problem row">
          <div class="col-8">
            <h3>Problem Description</h3>
            <div v-if="problem.type === 'pdf'">
              <object class="pdf" :data="problem.body" type="application/pdf">
                <p>
                  You may also <a :href="problem.body">download</a> the PDF.
                </p>
              </object>
            </div>
            <div v-else>
              <p>
                {{ problem.body }}
              </p>
            </div>
          </div>
          <div class="col-4">
            <h5>Submit Solution</h5>
            <div align="center" class="submit-form">
              <form v-on:submit.prevent="processUpload">
                <br>
                <input type="file" accept=".pl" ref="file">
                <br>
                <br>
                <button type="submit">Submit</button>
              </form>
            </div>
            <br>
            <div v-if="typeof upload !== 'null'">
              <div v-html="upload.message" />
            </div>
            <br>
            <br>
            <h5>Submission History</h5>
            <div v-if="submissions.length == 0" align="center">
              No submission.
            </div>
            <div v-else class="submission-history">
              <div class="row">
                <div class="col-9">Date</div>
                <div class="col-3" align="center">Scores</div>
              </div>
              <div class="ruler" />
              <div v-for="submission in submissions" :key="submission.id">
                <div class="row">
                  <div class="col-9">{{ renderNiceDate(submission.created_at) }}</div>
                  <div class="col-3" align="center">
                    <strong>{{ submission.scores }}pts</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { renderNiceDate } from '@/helpers/dateFormatter';
import { fetchApi } from '@/helpers/fetchApi';
import { redirectIfNotLoggedIn } from '@/helpers/loginChecker';
import { v4 as uuid } from 'uuid';

import NavBar from '@/components/NavBar.vue';

async function processAssignmentData(vm) {
  const fetch = fetchApi(vm);
  const assignmentId = vm.problem.assignment_id;
  const assignment = await fetch(`/assignments/${assignmentId}`, { withToken: true });

  vm.updateAssignment(assignment.data);
}

async function processProblemDetail(vm) {
  const fetch = fetchApi(vm);
  const problemId = vm.$route.params.problem_id;
  const problem = await fetch(`/problems/${problemId}`, { withToken: true });

  vm.updateProblem(problem.data);

  await processAssignmentData(vm);
}

async function processSubmissionHistory(vm) {
  const fetch = fetchApi(vm);
  const problemId = vm.$route.params.problem_id;
  const submissionHistory = await fetch(`/problems/${problemId}/submissions`, { withToken: true });

  vm.updateSubmissions(submissionHistory.data);
}

export default {
  name: 'problem_detail',
  components: {
    NavBar,
  },
  data: () => ({
    file: null,
    assignment: {},
    problem: {},
    submissions: [],
    upload: {
      message: '',
      status: null,
    },
  }),
  methods: {
    renderNiceDate,

    async processGrading(attributes = {}) {
      const fetch = fetchApi(this);

      const { bucketName, objectName } = attributes;
      const problemId = this.problem.id;
      const userId = this.$cookies.get('user').id;

      this.updateUpload({
        message: 'Grading submission...',
        status: 'grading',
      });

      const res = await fetch('/submissions', {
        method: 'POST',
        withToken: true,
        custom: {
          body: JSON.stringify({
            // eslint-disable-next-line @typescript-eslint/camelcase
            bucket_name: bucketName,
            // eslint-disable-next-line @typescript-eslint/camelcase
            object_name: objectName,
            // eslint-disable-next-line @typescript-eslint/camelcase
            problem_id: problemId,
            status: 'grading',
            // eslint-disable-next-line @typescript-eslint/camelcase
            user_id: userId,
          }),
        },
      });

      if (!res) {
        this.updateUpload({
          message: 'Failed to grade submission...',
          status: 'failed',
        });
        return;
      }

      const submissionId = res.data.id;

      const checkSubmission = setInterval(async () => {
        const submission = await fetch(`/submissions/${submissionId}`, {
          withToken: true,
        });

        if (submission.ok && submission.data.status === 'graded') {
          const msg = [
            'Submission graded.',
            `Scores: ${submission.data.scores}`,
            '',
            'Logs:',
            submission.data.logs.split('\n').join('<br>'),
          ];

          this.updateUpload({
            message: msg.join('<br>'),
            status: 'graded',
          });

          this.pushSubmission(submission.data);

          clearInterval(checkSubmission);
        }
      }, 3000);

      setTimeout(() => {
        clearInterval(checkSubmission);

        if (this.upload.status !== 'graded') {
          this.updateUpload({
            message: 'Failed to grade submission. It took too long.',
            status: 'failed',
          });
        }
      }, 16000);
    },
    async processUpload() {
      if (this.$refs.file.files.length === 0) {
        return;
      }

      const fetchIt = fetchApi(this);
      const userId = this.$cookies.get('user').id;

      const [sourceCode] = this.$refs.file.files;
      this.file = sourceCode;

      const bucketName = process.env.VUE_APP_TEARY_BUCKET_NAME;
      const fileName = `${uuid()}.pl`;
      const filePath = `${userId}/${this.problem.assignment_id}/${this.problem.id}/${fileName}`;

      this.updateUpload({
        message: 'Uploading submission...',
        status: 'uploading',
      });

      try {
        const uploadReq = await fetchIt('/files/upload/request', {
          method: 'POST',
          withToken: true,
          custom: {
            body: JSON.stringify({
              // eslint-disable-next-line @typescript-eslint/camelcase
              bucket_name: bucketName,
              // eslint-disable-next-line @typescript-eslint/camelcase
              object_name: filePath,
            }),
          },
        });

        const uploadUrl = uploadReq.data.url;

        const uploadRes = await fetch(uploadUrl, {
          method: 'PUT',
          body: this.file,
        });

        if (uploadRes.ok) {
          this.updateUpload({
            message: 'Submission uploaded!',
            status: 'uploaded',
          });

          this.$refs.file.value = '';

          const attributes = {
            bucketName: uploadReq.data.bucket_name,
            objectName: uploadReq.data.object_name,
          };
          setTimeout(() => this.processGrading(attributes), 1000);
        } else {
          this.updateUpload({
            message: 'Failed to upload submission. Please try again...',
            status: 'failed',
          });
        }
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.error(err);
        }
      }
    },
    async pushSubmission(submission) {
      this.submissions = [
        submission,
        ...this.submissions,
      ];

      await this.$nextTick();
    },
    async updateAssignment(assignment) {
      this.assignment = assignment;

      await this.$nextTick();
    },
    async updateProblem(problem) {
      this.problem = problem;

      await this.$nextTick();
    },
    async updateSubmissions(submissions) {
      this.submissions = submissions;

      await this.$nextTick();
    },
    async updateUpload(data = {}) {
      this.upload = data;

      await this.$nextTick();
    },
  },
  watch: {
    $route: {
      immediate: true,
      async handler() {
        if (await redirectIfNotLoggedIn(this)) {
          return;
        }

        await processProblemDetail(this);
        await processSubmissionHistory(this);
      },
    },
  },
};
</script>
